// Modules
import { Action, createReducer, on } from '@ngrx/store';

// Interfaces
import { IGetConfigListadoResponse, ILoggedUser } from '@shared/interfaces';

// Actions
import * as AuthActions from '@store/actions/auth.actions';

export interface AuthState {
  signingIn: boolean;
  loggedUser: ILoggedUser;
  signingOut: boolean;
  logingFailed: boolean;
  passwordChanged: boolean;
  passwordChanging: boolean;
  passwordRecovering: boolean;
  passwordChangeError: boolean;
  invalidPasswordReset: boolean;
  passwordChangeSuccess: boolean;
  userActivated: boolean;
  userActivating: boolean;
  invalidUserActivation: boolean;
  getConfigListadoRequested: boolean;
  getConfigListadoResponse: IGetConfigListadoResponse;
  setConfigListadoRequested: boolean;
}

export const initialAuthState: AuthState = {
  signingIn: false,
  loggedUser: null,
  signingOut: false,
  logingFailed: false,
  passwordChanged: false,
  passwordChanging: false,
  passwordRecovering: false,
  passwordChangeError: false,
  invalidPasswordReset: false,
  passwordChangeSuccess: false,
  userActivated: false,
  userActivating: false,
  invalidUserActivation: false,
  getConfigListadoRequested: false,
  getConfigListadoResponse: null,
  setConfigListadoRequested: false,
};

const authReducer = createReducer(
  initialAuthState,
  // Check for Logged User
  on(AuthActions.checkForLoggedUser, (state, action) => ({
    ...state,
    loggedUser: action.payload,
  })),
  // Login
  on(AuthActions.loginFailed, state => ({
    ...state,
    signingIn: false,
    logingFailed: true,
  })),
  on(AuthActions.loginRequested, state => ({
    ...state,
    signingIn: true,
    logingFailed: false,
    passwordChanged: false,
  })),
  on(AuthActions.loginSuccessful, (state, action) => ({
    ...state,
    loggedUser: action.payload,
    currentUser: action.payload,
    signingIn: false,
  })),
  // Login First Step
  on(AuthActions.loginMicrosoftRequested, state => ({
    ...state,
    logingFailed: false,
    passwordChanged: false,
  })),
  on(AuthActions.loginMicrosoftFailure, state => ({
    ...state,
    signingIn: false,
    logingFailed: true,
  })),
  // Login MSToken
  on(AuthActions.loginMSTokenRequested, state => ({
    ...state,
    signingIn: true,
    logingFailed: false,
    passwordChanged: false,
  })),
  on(AuthActions.loginMSTokenSuccess, (state, action) => ({
    ...state,
    loggedUser: action.payload,
    currentUser: action.payload,
    signingIn: false,
  })),
  // Logout
  on(AuthActions.logoutRequested, state => ({ ...state, signingOut: true })),
  on(AuthActions.logoutSuccessful, state => ({
    ...state,
    signingOut: false,
    currentUser: undefined,
    loggedUser: undefined,
    signingIn: false,
  })),
  // Password Change
  on(AuthActions.passwordChangeFailed, state => ({
    ...state,
    passwordChanging: false,
    passwordChangeError: true,
    passwordChangeSuccess: false,
  })),
  on(AuthActions.passwordChangeRequested, state => ({
    ...state,
    passwordChanging: true,
    passwordChangeError: false,
    passwordChangeSuccess: false,
  })),
  on(AuthActions.passwordChangeSuccessful, state => ({
    ...state,
    passwordChanging: false,
    passwordChangeError: false,
    passwordChangeSuccess: true,
  })),
  on(AuthActions.resetPasswordChangeStatuses, state => ({
    ...state,
    passwordChanging: false,
    passwordChangeError: false,
    passwordChangeSuccess: false,
  })),
  // Password Recovery
  on(AuthActions.passwordResetFailed, state => ({
    ...state,
    passwordChanged: false,
    passwordChanging: false,
  })),
  on(AuthActions.passwordResetRequested, state => ({
    ...state,
    passwordChanged: false,
    passwordChanging: true,
  })),
  on(AuthActions.passwordResetSuccessful, state => ({
    ...state,
    passwordChanged: true,
    passwordChanging: false,
  })),
  on(AuthActions.passwordRecoveryResponse, state => ({
    ...state,
    passwordRecovering: false,
  })),
  on(AuthActions.passwordRecoveryRequested, state => ({
    ...state,
    passwordRecovering: true,
  })),
  on(AuthActions.verifyPasswordResetResponse, (state, action) => ({
    ...state,
    invalidPasswordReset: action.payload.invalid,
  })),
  // Activation
  on(AuthActions.activationFailed, state => ({
    ...state,
    userActivated: false,
    userActivating: false,
  })),
  on(AuthActions.activationRequested, state => ({
    ...state,
    userActivated: false,
    userActivating: true,
  })),
  on(AuthActions.activationSuccessful, state => ({
    ...state,
    userActivated: true,
    userActivating: false,
  })),
  on(AuthActions.verifyActivationResponse, (state, action) => ({
    ...state,
    invalidUserActivation: action.payload.invalid,
  })),
  on(AuthActions.getConfigListadoRequested, state => ({
    ...state,
    getConfigListadoRequested: true,
    getConfigListadoResponse: null,
  })),
  on(AuthActions.getConfigListadoResponse, (state, action) => ({
    ...state,
    getConfigListadoRequested: false,
    getConfigListadoResponse: action.payload,
  })),
  on(AuthActions.setConfigListadoRequested, state => ({
    ...state,
    setConfigListadoRequested: true,
  })),
  on(AuthActions.setConfigListadoResponse, state => ({
    ...state,
    setConfigListadoRequested: false,
  }))
);

export function reducer(
  state: AuthState | undefined,
  action: Action
): AuthState {
  return authReducer(state, action);
}
